import React, { useContext, useEffect, useState } from "react"

import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/core"

import Spinner from "~tailwindui/Basics/Spinner"

import EventEmitter from "src/EventEmitter"
import classNames from "src/classNames"

import CloudroomContext from "components/ChimeCloudroom/CloudroomContext"
import useLocalScreenShare from "components/ChimeCloudroom/hooks/useLocalScreenShare"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"

const ScreenShareButton = () => {
  const { channel, roster, localUser, session } = useContext(CloudroomContext)

  const disabledInitialValue =
    session.studentScreenShareDisabled ||
    session.screenShareRejectedStudentIds.includes(localUser.id)
  const [localScreenShareDisabled, setLocalScreenShareDisabled] =
    useState<boolean>(localUser.type === "Student" && disabledInitialValue)
  const [screenShareRequestPending, setScreenShareRequestPending] =
    useState<boolean>(false)
  const [studentScreenShareMessage, setStudentScreenShareMessage] = useState<
    string | null
  >()

  const { startLocalScreenShare, stopLocalScreenShare } = useLocalScreenShare()

  const screenshareErrorMessage =
    "If you did not click cancel, something has gone wrong. Please ensure you are using the latest version of Chrome on your desktop/laptop."

  useEffect(() => {
    const onScreenShareResponse = data => {
      if (data.approved) {
        startLocalScreenShare(() =>
          setStudentScreenShareMessage(screenshareErrorMessage)
        )
      } else {
        setLocalScreenShareDisabled(true)
        setStudentScreenShareMessage(
          "The tutor has denied your screen share request"
        )
      }
      setScreenShareRequestPending(false)
    }
    EventEmitter.subscribe("screen_share_response", onScreenShareResponse)

    return () =>
      EventEmitter.unsubscribe("screen_share_response", onScreenShareResponse)
  }, [startLocalScreenShare])

  const localAttendee = roster[session.chimeJoinInfo.attendee.attendeeId]
  const screenSharing = localAttendee?.sharingContent

  const requestScreenShare = () => {
    if (["Tutor", "Teacher"].includes(localUser.type)) {
      startLocalScreenShare(() =>
        setStudentScreenShareMessage(screenshareErrorMessage)
      )
    } else {
      setScreenShareRequestPending(true)
      channel.perform("screen_share_request", { userId: localUser.id })
    }
  }

  return (
    <>
      {/* button to start or stop screenshare */}
      {!localScreenShareDisabled &&
        (screenSharing ? (
          <button
            className={classNames(
              "btn solid red",
              localUser.type === "Admin" && "btn-danger",
              localUser.type === "Teacher" &&
                "rounded bg-brand-700 p-1 px-3 font-semibold"
            )}
            onClick={stopLocalScreenShare}
          >
            Stop Screen Share
          </button>
        ) : (
          <button
            className={classNames(
              "btn solid blue mb-1 text-white",
              localUser.type === "Admin" && "btn-info",
              localUser.type === "Teacher" &&
                "rounded bg-info-700 px-3 py-1 font-semibold",
              "flex items-center disabled:opacity-75 disabled:hover:bg-info-500 disabled:hover:text-white disabled:hover:opacity-75"
            )}
            onClick={requestScreenShare}
            disabled={screenShareRequestPending}
          >
            Share Your Screen
            {screenShareRequestPending && (
              <Spinner size="medium" className="ml-3 mr-0" />
            )}
          </button>
        ))}

      {/* modal to display screenshare-related message */}
      {studentScreenShareMessage &&
        (localUser.type === "Teacher" ? (
          <Modal isOpen>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Screen Share Failed</ModalHeader>
              <ModalBody>
                <p>{studentScreenShareMessage}</p>
                <div className="flex">
                  <button
                    className="mr-4 rounded bg-info-700 px-3 py-1 font-semibold text-white"
                    onClick={() => {
                      setStudentScreenShareMessage(null)
                    }}
                  >
                    Okay
                  </button>
                </div>
              </ModalBody>
            </ModalContent>
          </Modal>
        ) : (
          <ModalWithProvidedBody
            isOpen
            hideTrigger
            modalClassName="bootstrap-modal"
            modalTitle="Screen Share Failed"
          >
            {({ closeModal }) => (
              <>
                <div className="modal-body">{studentScreenShareMessage}</div>
                <div className="modal-footer">
                  <button
                    className="btn solid blue btn-info"
                    onClick={() => {
                      closeModal()
                      setStudentScreenShareMessage(null)
                    }}
                  >
                    Okay
                  </button>
                </div>
              </>
            )}
          </ModalWithProvidedBody>
        ))}
    </>
  )
}

export default ScreenShareButton
